'use client'
import { Button } from '@/components/v2/Button'
import { FileCode2, GraduationCap, ListTree } from 'lucide-react'
import * as React from 'react'
import { useForcedViewTheme } from '../theme'
import { cn } from '@/utils/style'
import Link from '@/components/Link'
import { useModal } from '@/app/Providers/ModalContext'
import XmlViewer from '@/components/XmlViewer'
import SvgLoadingSpin from '@/components/SvgLoadingSping'
import { useDrawerBottom } from '@/app/(dashboard)/assinar/[id]/Template/Details/DrawerBottom'
import Card from '@/components/Card'
import Tooltip from '@/components/Tooltip'
import { toast } from '@/hooks/useToast'
import { PublicViewFieldsFragment } from '@/graphql/generated/graphql'
import { useGenericDocumentDownload } from '@/hooks/useGenericDocumentDownload'

type ActionsProps = {
  publicViewFields: PublicViewFieldsFragment
}

function IconOrLoading({
  loading,
  children,
}: {
  loading: boolean
  children: React.ReactNode
}) {
  if (loading) {
    return <SvgLoadingSpin className="w-4 h-4" color="dark" />
  } else {
    return <>{children}</>
  }
}

export default function Actions(props: ActionsProps) {
  const theme = useForcedViewTheme()
  const {
    downloadGenericDocument: downloadToSee,
    isDownloading: isDownloadingToSee,
  } = useGenericDocumentDownload()
  const { downloadGenericDocument, isDownloading } =
    useGenericDocumentDownload()
  const {
    downloadGenericDocument: downloadVisualRepresentation,
    isDownloading: isDownloadingVisualRepresentation,
  } = useGenericDocumentDownload()

  const { setContent, Drawer } = useDrawerBottom('xml_viewer')
  const { setContent: setModal, close } = useModal()

  // const awaitFor = async (promise: () => Promise<any>) => {
  //   setModal(
  //     <Card className="p-4 flex flex-col gap-2 items-center justify-center">
  //       <p>Carregando...</p>
  //       <SvgLoadingSpin color="accent" />
  //     </Card>,
  //   )
  //   await promise()
  //   close()
  // }

  async function downloadXml() {
    downloadGenericDocument({
      id: props.publicViewFields.id,
      shouldDownload: true,
      securityCode: props.publicViewFields.securityCode,
    })
  }

  async function showXml() {
    const xml = await downloadToSee({
      id: props.publicViewFields.id,
      shouldDownload: false,
      securityCode: props.publicViewFields.securityCode,
    })
    console.log({ xml })
    if (xml) {
      setContent(<XmlViewer xml={xml} />)
    }
  }

  return (
    <div className="px-4 lg:px-0 max-w-6xl mx-auto flex-col md:flex-row mt-4 flex gap-4 items-center justify-between">
      <div className="flex gap-4 items-center">
        <Button
          disabled={isDownloading}
          onClick={() => {
            // downloadDocumentAcademicDocumentation()
            downloadXml()
          }}
          className="flex gap-2 items-center bg-white"
          variant={'outline'}
        >
          <IconOrLoading loading={isDownloading}>
            <FileCode2 className="w-4 h-4" />
          </IconOrLoading>
          <span>Download do {props.publicViewFields.documentTypeName}</span>
        </Button>
        <Link
          action={async () => {
            // const xml = await downloadToSee(false)
            // console.log({ xml })
            // if (xml) {
            //   setContent(<XmlViewer xml={xml} />)
            // }
            showXml()
          }}
        >
          <Button
            className="flex gap-2 items-center bg-white"
            variant={'outline'}
            disabled={isDownloadingToSee}
          >
            <IconOrLoading loading={isDownloadingToSee}>
              <ListTree className="w-4 h-4" />
            </IconOrLoading>
            <span>Visualizar XML</span>
          </Button>
        </Link>
      </div>
      {props.publicViewFields.isVisualRepresentationEnabled && (
        <Tooltip
          title={
            props.publicViewFields?.isVisualRepresentationEnabled
              ? 'Ver representação visual'
              : `Este ${props.publicViewFields.documentTypeName.toLowerCase()} ainda não possui uma representação visual`
          }
        >
          <Button
            id="secondary-button"
            className={cn(
              'flex gap-2 items-center bg-white',
              `hover:bg-[${theme.publicViewPrimaryColor}20]`,
              {
                'opacity-50':
                  !props.publicViewFields?.isVisualRepresentationEnabled,
              },
            )}
            style={{
              borderColor: theme.publicViewSecondaryColor,
              color: theme.publicViewSecondaryColor,
            }}
            disabled={isDownloadingVisualRepresentation}
            variant={'destructive'}
            onClick={() => {
              if (!props.publicViewFields?.isVisualRepresentationEnabled) {
                toast({
                  title: `Este ${props.publicViewFields.documentTypeName.toLowerCase()} ainda não possui representação visual`,
                })
                return
              }
              downloadVisualRepresentation({
                id: props.publicViewFields.id,
                shouldDownload: true,
                securityCode: props.publicViewFields.securityCode,
                type: 'visual-representation',
              })
            }}
            // disabled={!docFound?.visualRepresentationPath}
          >
            <IconOrLoading loading={isDownloadingVisualRepresentation}>
              <GraduationCap className="w-4 h-4" />
            </IconOrLoading>
            <span>
              {props.publicViewFields?.isVisualRepresentationEnabled
                ? `Download da Representação Visual do ${props.publicViewFields.documentTypeName}`
                : `Representação Visual do ${props.publicViewFields.documentTypeName} indisponível`}
            </span>
          </Button>
        </Tooltip>
      )}
      <Drawer />
      <style>
        {`
          #secondary-button:hover {
            background-color: ${theme.publicViewSecondaryColor}05;
          }
        `}
      </style>
    </div>
  )
}
